// MARK://dhk8/pos/components/DataTable
// Ported & adapted from sc-restaurant/packages/design-system/components/DataTable.
// Self-contained for dhk8-pos: zero radius / no shadows / hierarchy by opacity,
// plus the *mobile/compact variant* the POS tablet needs (534×738 portrait, DPR
// 1.5, data-density="compact"):
//   - client-side pagination
//   - click-to-sort headers (sortable columns only)
//   - on narrow viewports, columns flagged `hideOnNarrow` collapse, and each row
//     can render a "card" layout (`mobileCards` prop) where hidden columns appear
//     as label/value pairs under the primary cell.
// Touch targets ≥44pt: header buttons, pager buttons and clickable rows are min
// 44px tall via the inline stylesheet below.
//
// Styling uses the shared design tokens (var(--space-*), var(--text-*), etc.)
// already provided by @dockethub/ui's theme-nintaigo.

import { useMemo, useState, type ReactNode, type CSSProperties } from 'react'

export type ColumnAlign = 'start' | 'center' | 'end'

export interface ColumnDef<T> {
  key: string
  label: ReactNode
  width?: string
  minWidth?: string
  align?: ColumnAlign
  numeric?: boolean
  /** Tooltip on the header cell. */
  hint?: string
  /** If true, this column is hidden on narrow viewports (and shown inside the row card if `mobileCards`). */
  hideOnNarrow?: boolean
  /** If true, the header becomes a sort toggle. Provide `sortValue` for non-trivial sorts. */
  sortable?: boolean
  /** Returns a comparable value for sorting. Defaults to the rendered cell when it's a string/number. */
  sortValue?: (row: T) => string | number
}

export interface DataTableProps<T> {
  columns: ColumnDef<T>[]
  rows: T[]
  getRowKey: (row: T, index: number) => string | number
  renderCell?: (row: T, col: ColumnDef<T>, index: number) => ReactNode
  onRowClick?: (row: T, index: number) => void
  /** Rows per page. 0 = no pagination (show all). Default 20. */
  pageSize?: number
  /** Initial sort: { key, dir }. */
  initialSort?: { key: string; dir: 'asc' | 'desc' }
  zebra?: boolean
  loading?: boolean
  emptyState?: ReactNode
  toolbar?: ReactNode
  ariaLabel?: string
  className?: string
  /**
   * On narrow viewports, render each row as a card (primary cell + hidden columns
   * as label/value rows). Default true. The narrow breakpoint is a CSS-only thing
   * (container width ≤ 560px); we always render BOTH layouts and let CSS pick.
   */
  mobileCards?: boolean
  /** Which column is the "primary" one shown big in the mobile card. Default: first column. */
  primaryKey?: string
}

function defaultSortValue<T>(row: T, col: ColumnDef<T>, renderCell?: DataTableProps<T>['renderCell']): string | number {
  if (col.sortValue) return col.sortValue(row)
  const raw = renderCell ? renderCell(row, col, 0) : (row as unknown as Record<string, unknown>)[col.key]
  if (typeof raw === 'number') return raw
  if (typeof raw === 'string') return raw.toLowerCase()
  return String(raw ?? '').toLowerCase()
}

export function DataTable<T>(props: DataTableProps<T>): ReturnType<typeof DataTableImpl> {
  return DataTableImpl(props)
}

function DataTableImpl<T>({
  columns,
  rows,
  getRowKey,
  renderCell,
  onRowClick,
  pageSize = 20,
  initialSort,
  zebra = false,
  loading,
  emptyState,
  toolbar,
  ariaLabel,
  className,
  mobileCards = true,
  primaryKey,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(initialSort ?? null)
  const [page, setPage] = useState(0)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const col = columns.find((c) => c.key === sort.key)
    if (!col) return rows
    const copy = rows.slice()
    copy.sort((a, b) => {
      const va = defaultSortValue(a, col, renderCell)
      const vb = defaultSortValue(b, col, renderCell)
      if (va < vb) return sort.dir === 'asc' ? -1 : 1
      if (va > vb) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return copy
  }, [rows, sort, columns, renderCell])

  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1
  const safePage = Math.min(page, pageCount - 1)
  const visible = pageSize > 0 ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize) : sorted

  const toggleSort = (key: string) => {
    setPage(0)
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: 'asc' }
      if (cur.dir === 'asc') return { key, dir: 'desc' }
      return null
    })
  }

  const pk = primaryKey ?? columns[0]?.key
  const cell = (row: T, col: ColumnDef<T>, idx: number): ReactNode =>
    renderCell ? renderCell(row, col, idx) : ((row as unknown as Record<string, ReactNode>)[col.key] ?? null)

  return (
    <div className={`dhk8-dt${className ? ' ' + className : ''}`}>
      {toolbar && <div className="dhk8-dt__toolbar">{toolbar}</div>}

      {/* ── Wide layout: real table ── */}
      <div className="dhk8-dt__scroll" data-layout="table">
        <table className="dhk8-dt__table" aria-label={ariaLabel}>
          <colgroup>
            {columns.map((c) => (
              <col
                key={c.key}
                style={{ width: c.width, minWidth: c.minWidth } as CSSProperties}
                data-narrow-hidden={c.hideOnNarrow ? '' : undefined}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key
                return (
                  <th
                    key={c.key}
                    data-align={c.align ?? 'start'}
                    data-narrow-hidden={c.hideOnNarrow ? '' : undefined}
                    title={c.hint}
                  >
                    {c.sortable ? (
                      <button type="button" className="dhk8-dt__sortbtn" onClick={() => toggleSort(c.key)} aria-label={`Ordenar por ${typeof c.label === 'string' ? c.label : c.key}`}>
                        <span>{c.label}</span>
                        <span className="dhk8-dt__sortarrow" aria-hidden>{active ? (sort!.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`} className="dhk8-dt__row--sk">
                  {columns.map((c) => (
                    <td key={c.key}><span className="dhk8-dt__skel" /></td>
                  ))}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="dhk8-dt__empty">{emptyState ?? <span>Sin registros.</span>}</td>
              </tr>
            ) : (
              visible.map((row, idx) => (
                <tr
                  key={getRowKey(row, idx)}
                  onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                  data-clickable={onRowClick ? '' : undefined}
                  className={zebra && idx % 2 === 1 ? 'dhk8-dt__row--alt' : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      data-align={c.align ?? 'start'}
                      data-numeric={c.numeric ? '' : undefined}
                      data-narrow-hidden={c.hideOnNarrow ? '' : undefined}
                    >
                      {cell(row, c, idx)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Narrow layout: row cards ── */}
      {mobileCards && (
        <div className="dhk8-dt__cards" data-layout="cards">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <div key={`csk-${i}`} className="dhk8-dt__card dhk8-dt__card--sk"><span className="dhk8-dt__skel" /></div>)
          ) : visible.length === 0 ? (
            <div className="dhk8-dt__empty">{emptyState ?? <span>Sin registros.</span>}</div>
          ) : (
            visible.map((row, idx) => {
              const primaryCol = columns.find((c) => c.key === pk) ?? columns[0]!
              const rest = columns.filter((c) => c.key !== primaryCol.key)
              return (
                <div
                  key={getRowKey(row, idx)}
                  className="dhk8-dt__card"
                  onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                  data-clickable={onRowClick ? '' : undefined}
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row, idx) } } : undefined}
                >
                  <div className="dhk8-dt__card-primary">{cell(row, primaryCol, idx)}</div>
                  <dl className="dhk8-dt__card-kv">
                    {rest.map((c) => (
                      <div key={c.key} className="dhk8-dt__card-kv-row">
                        <dt>{c.label}</dt>
                        <dd data-numeric={c.numeric ? '' : undefined}>{cell(row, c, idx)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )
            })
          )}
        </div>
      )}

      {/* ── Pager ── */}
      {pageSize > 0 && pageCount > 1 && !loading && (
        <div className="dhk8-dt__pager">
          <button type="button" disabled={safePage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="dhk8-dt__pgbtn">‹ Anterior</button>
          <span className="dhk8-dt__pginfo">Página {safePage + 1} de {pageCount} · {sorted.length} registros</span>
          <button type="button" disabled={safePage >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} className="dhk8-dt__pgbtn">Siguiente ›</button>
        </div>
      )}

      <style>{dataTableStyles}</style>
    </div>
  )
}

// Toolbar helpers (composition).
export function DataTableToolbar({ children }: { children: ReactNode }) {
  return <div className="dhk8-dt__toolbar-content">{children}</div>
}
export function DataTableSearch(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input type="search" className="dhk8-dt__search" {...props} />
}
export function DataTableActions({ children }: { children: ReactNode }) {
  return <div className="dhk8-dt__toolbar-actions">{children}</div>
}

const dataTableStyles = `
  /* HIG-green data table: rounded container, soft hairline rows, hover tint, the
     narrow-viewport "cards" layout reads like Apple grouped list cells. Reads the
     host app's --pos-* tokens; legacy aliases as fallback. No harsh borders. */
  .dhk8-dt { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-dt__toolbar { display:flex; }
  .dhk8-dt__toolbar-content { display:flex; gap:var(--pos-space-2,8px); align-items:center; flex-wrap:wrap; width:100%; }
  .dhk8-dt__toolbar-actions { display:flex; gap:var(--pos-space-2,8px); margin-left:auto; }
  .dhk8-dt__search { border:1px solid var(--pos-separator-strong,var(--separator,rgba(255,255,255,.14))); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); background:var(--pos-canvas,var(--bg-canvas,#0F1411)); color:var(--pos-fg,var(--text-primary,#E8EFE9)); font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); border-radius:var(--pos-radius-md,8px); min-height:44px; transition:border-color var(--pos-duration-fast,120ms) var(--pos-ease-out,ease), box-shadow var(--pos-duration-fast,120ms) var(--pos-ease-out,ease); }
  .dhk8-dt__search:focus-visible { outline:none; border-color:var(--pos-tint,var(--accent,#1F8B3A)); box-shadow:0 0 0 3px var(--pos-tint-soft,color-mix(in oklch,#1F8B3A 12%,transparent)); }

  .dhk8-dt__scroll { overflow-x:auto; border:1px solid var(--pos-separator,var(--separator-opaque,rgba(255,255,255,.07))); border-radius:var(--pos-radius-lg,12px); background:var(--pos-surface,var(--bg-surface,#161C18)); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-dt__table { width:100%; border-collapse:collapse; font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); font-variant-numeric:tabular-nums; }
  .dhk8-dt__table th, .dhk8-dt__table td { padding:var(--pos-space-3,12px); text-align:left; border-bottom:1px solid var(--pos-separator,var(--separator,rgba(255,255,255,.07))); vertical-align:top; }
  .dhk8-dt__table tbody tr:last-child td { border-bottom:none; }
  .dhk8-dt__table th { color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); letter-spacing:var(--pos-tracking-wide,.04em); text-transform:uppercase; background:var(--pos-elevated,var(--bg-surface,#1E2520)); position:sticky; top:0; z-index:1; }
  .dhk8-dt__table thead th { border-bottom-color:var(--pos-separator-strong,var(--separator,rgba(255,255,255,.14))); }
  .dhk8-dt__table td { color:var(--pos-fg-secondary,var(--text-secondary,#B6BFB8)); }
  .dhk8-dt__table td[data-align="end"], .dhk8-dt__table th[data-align="end"] { text-align:right; }
  .dhk8-dt__table td[data-align="center"], .dhk8-dt__table th[data-align="center"] { text-align:center; }
  .dhk8-dt__table td[data-numeric] { font-variant-numeric:tabular-nums; color:var(--pos-fg,var(--text-primary,#E8EFE9)); }
  .dhk8-dt__sortbtn { display:inline-flex; align-items:center; gap:var(--pos-space-1,4px); background:none; border:none; color:inherit; font:inherit; font-weight:var(--pos-weight-medium,500); letter-spacing:inherit; text-transform:inherit; cursor:pointer; padding:0; min-height:24px; border-radius:var(--pos-radius-xs,4px); }
  .dhk8-dt__sortbtn:hover { color:var(--pos-fg-secondary,var(--text-secondary,#B6BFB8)); }
  .dhk8-dt__sortarrow { opacity:.55; }
  .dhk8-dt__row--alt td { background:color-mix(in oklch,var(--pos-fg,#E8EFE9) 3%,transparent); }
  .dhk8-dt__table tr[data-clickable] { cursor:pointer; transition:background var(--pos-duration-fast,120ms) var(--pos-ease-out,ease); }
  .dhk8-dt__table tr[data-clickable]:hover td { background:var(--pos-tint-soft,color-mix(in oklch,#1F8B3A 12%,transparent)); }
  .dhk8-dt__table tr[data-clickable]:active td { background:var(--pos-tint-hover,color-mix(in oklch,#1F8B3A 18%,transparent)); }
  .dhk8-dt__table tr[data-clickable] td { min-height:44px; }
  .dhk8-dt__empty { padding:var(--pos-space-6,24px); text-align:center; font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); }
  .dhk8-dt__skel { display:block; height:12px; width:80%; background:color-mix(in oklch,var(--pos-fg,#E8EFE9) 8%,transparent); border-radius:var(--pos-radius-pill,999px); animation:dhk8-dt-pulse 1.4s ease-in-out infinite; }
  @keyframes dhk8-dt-pulse { 0%,100%{opacity:.4} 50%{opacity:.8} }
  .dhk8-dt__pager { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-2,8px); flex-wrap:wrap; }
  .dhk8-dt__pgbtn { min-height:44px; padding:var(--pos-space-2,8px) var(--pos-space-4,16px); border:1px solid var(--pos-separator-strong,var(--separator,rgba(255,255,255,.14))); background:var(--pos-elevated,var(--bg-surface,#1E2520)); color:var(--pos-fg,var(--text-primary,#E8EFE9)); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); border-radius:var(--pos-radius-pill,999px); cursor:pointer; transition:background var(--pos-duration-fast,120ms) var(--pos-ease-out,ease), transform var(--pos-duration-fast,120ms) var(--pos-ease-out,ease); }
  .dhk8-dt__pgbtn:hover:not(:disabled) { background:var(--pos-surface,var(--bg-surface,#161C18)); }
  .dhk8-dt__pgbtn:active:not(:disabled) { transform:scale(0.97); }
  .dhk8-dt__pgbtn:disabled { opacity:.4; cursor:default; }
  .dhk8-dt__pginfo { font-family:var(--font-display,inherit); font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); }

  /* Card layout — Apple grouped list cells: rounded, hairline, soft press tint. */
  .dhk8-dt__cards { display:none; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-dt__card { border:1px solid var(--pos-separator,var(--separator-opaque,rgba(255,255,255,.07))); border-radius:var(--pos-radius-lg,12px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px); display:flex; flex-direction:column; gap:var(--pos-space-2,8px); background:var(--pos-surface,var(--bg-surface,#161C18)); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); transition:background var(--pos-duration-fast,120ms) var(--pos-ease-out,ease), transform var(--pos-duration-fast,120ms) var(--pos-ease-out,ease); }
  .dhk8-dt__card[data-clickable] { cursor:pointer; }
  .dhk8-dt__card[data-clickable]:hover { background:var(--pos-elevated,var(--surface-elevated,#1E2520)); }
  .dhk8-dt__card[data-clickable]:active { background:var(--pos-tint-soft,color-mix(in oklch,#1F8B3A 12%,transparent)); transform:scale(0.99); }
  .dhk8-dt__card[data-clickable]:focus-visible { outline:none; box-shadow:0 0 0 3px var(--pos-tint-soft,color-mix(in oklch,#1F8B3A 12%,transparent)); }
  .dhk8-dt__card-primary { font-family:var(--font-display,inherit); font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,var(--text-primary,#E8EFE9)); }
  .dhk8-dt__card-kv { margin:0; display:flex; flex-direction:column; gap:var(--pos-space-1,4px); }
  .dhk8-dt__card-kv-row { display:flex; justify-content:space-between; gap:var(--pos-space-3,12px); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); }
  .dhk8-dt__card-kv-row dt { color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); text-transform:uppercase; letter-spacing:var(--pos-tracking-wide,.04em); font-size:var(--pos-text-xxs,11px); }
  .dhk8-dt__card-kv-row dd { margin:0; color:var(--pos-fg-secondary,var(--text-secondary,#B6BFB8)); text-align:right; font-variant-numeric:tabular-nums; }

  /* Narrow viewport → cards on, table off; hide-on-narrow columns dropped (defensive). */
  @media (max-width: 560px) {
    .dhk8-dt__scroll[data-layout="table"] { display:none; }
    .dhk8-dt__cards[data-layout="cards"] { display:flex; }
  }
  [data-density="compact"] .dhk8-dt__table th, [data-density="compact"] .dhk8-dt__table td { padding:var(--pos-space-2,8px) var(--pos-space-3,12px); }
  @media (max-width: 720px) {
    .dhk8-dt__table th[data-narrow-hidden], .dhk8-dt__table td[data-narrow-hidden] { display:none; }
  }
`
